import { UC_EVENT_NAME, UC_INITIALIZED_EVENT, UC_INITIAL_PAGE_LOAD_ACTION, UCConsentServiceNames } from './const';
import { UCConsentEventDetail, UCServiceInfo, UCUI } from './types';

export class UserCentricsService {
    public static subscribeToUCInitialized(callback: () => void | Promise<void>) {
        return UserCentricsService.subscribeToUCEvents(callback, callback);
    }

    public static subscribeToConsentSettled(callback: () => void | Promise<void>) {
        const runIfConsentIsSettled = async (): Promise<void> => {
            if (UserCentricsService.getUCUI()?.isConsentRequired() !== false) {
                return;
            }

            await callback();
        };

        return UserCentricsService.subscribeToUCEvents(callback, runIfConsentIsSettled);
    }

    private static subscribeToUCEvents(onConsentChange: () => void | Promise<void>, onInitialized: () => void | Promise<void>) {
        if (typeof window === 'undefined') {
            return;
        }

        const handleConsentEvent = (event: Event): void => {
            const { detail } = event as CustomEvent<UCConsentEventDetail | undefined>;
            if (detail?.action !== UC_INITIAL_PAGE_LOAD_ACTION) {
                onConsentChange();
            }
        };

        window.addEventListener(UC_EVENT_NAME, handleConsentEvent);

        if (UserCentricsService.isInitialized()) {
            onInitialized();
        } else {
            window.addEventListener(UC_INITIALIZED_EVENT, onInitialized, { once: true });
        }

        return () => {
            window.removeEventListener(UC_EVENT_NAME, handleConsentEvent);
            window.removeEventListener(UC_INITIALIZED_EVENT, onInitialized);
        };
    }

    public static hasLoaded(): boolean {
        return !!UserCentricsService.getUCUI();
    }

    public static isInitialized(): boolean {
        return !!UserCentricsService.getUCUI()?.isInitialized();
    }

    public static async hasServiceConsent(serviceName: UCConsentServiceNames): Promise<boolean | undefined> {
        const services = await UserCentricsService.getServicesBaseInfo();
        return services?.some(service => service.name === serviceName && !!service.consent.status);
    }

    public static async hasFunctionalConsent(): Promise<boolean | undefined> {
        const services = await UserCentricsService.getServicesBaseInfo();
        return services?.some(service => service.categorySlug === 'functional' && service.consent.status);
    }

    public static async hasAnyNonEssentialConsent(): Promise<boolean | undefined> {
        const services = await UserCentricsService.getServicesBaseInfo();
        // Essential services are always consented, so they are excluded here.
        return services?.some(service => service.categorySlug !== 'essential' && service.consent.status);
    }

    public static async getControllerId(): Promise<string | undefined> {
        const ucUI = UserCentricsService.getUCUI();
        if (!ucUI) {
            return;
        }
        return ucUI.getControllerId();
    }

    private static getUCUI(): UCUI | undefined {
        if (typeof window === 'undefined') {
            return;
        }
        return window.UC_UI;
    }

    private static async getServicesBaseInfo(): Promise<UCServiceInfo[] | undefined> {
        return UserCentricsService.getUCUI()?.getServicesBaseInfo();
    }
}
